<script lang="ts">
/**
 * A member's reply as formatted text: bold, italics, inline and fenced code, lists, links and headings, from the
 * Markdown the member wrote (lib/markdown.ts). Drawn with Vue's own nodes, built from that tree, so a message's
 * text is only ever text: raw HTML in it shows as the characters it was written in and creates no element.
 *
 * At the bubble's size and in its colours (ZhChatThread): a heading is a bold line, not a page heading, and code
 * sits on the raised panel in the mono face. Links, a web address written bare among them, are ZhLink: the system
 * cyan, opening in a new tab. A single newline stays a line break, as the member wrote it.
 */
import { computed, defineComponent, h, type VNode, type VNodeArrayChildren } from 'vue'
import { type MarkdownBlock, type MarkdownInline, parseMarkdown } from '../lib/markdown'
import ZhLink from './ZhLink.vue'

function inlineNode(node: MarkdownInline): VNodeArrayChildren {
    switch (node.kind) {
        case 'text':
            return [node.text]
        case 'break':
            // The newline after the break keeps the text as written for anything that reads it, not the eye.
            return [h('br'), '\n']
        case 'strong':
            return [h('strong', inline(node.children))]
        case 'em':
            return [h('em', inline(node.children))]
        case 'code':
            return [h('code', { class: 'zh-md__code' }, node.text)]
        case 'link':
            return [h(ZhLink, { href: node.href }, () => inline(node.children))]
    }
}

function inline(nodes: readonly MarkdownInline[]): VNodeArrayChildren {
    return nodes.flatMap(inlineNode)
}

/** An item that is one paragraph is drawn as its text, so a short list reads as a list rather than as paragraphs. */
function item(blocks: readonly MarkdownBlock[]): VNodeArrayChildren {
    const [only] = blocks
    return blocks.length === 1 && only?.kind === 'paragraph' ? inline(only.children) : blocks.map(block)
}

function block(node: MarkdownBlock): VNode {
    switch (node.kind) {
        case 'paragraph':
            return h('p', inline(node.children))
        case 'heading':
            return h('p', { class: 'zh-md__heading' }, inline(node.children))
        case 'code':
            return h('pre', { class: 'zh-md__pre' }, [h('code', node.text)])
        case 'rule':
            return h('hr', { class: 'zh-md__rule' })
        case 'list':
            return h(
                node.ordered ? 'ol' : 'ul',
                { class: 'zh-md__list', start: node.ordered && node.start !== 1 ? node.start : undefined },
                node.items.map(blocks => h('li', item(blocks))),
            )
    }
}

export default defineComponent({
    name: 'ZhMarkdown',
    props: {
        /** What the member wrote. */
        text: { type: String, required: true },
    },
    setup(props) {
        const blocks = computed(() => parseMarkdown(props.text))
        return () => h('div', { class: 'zh-md' }, blocks.value.map(block))
    },
})
</script>

<style scoped>
p,
ul,
ol,
pre {
    margin: 0;
}

/* Blocks a short step apart, so a reply reads as paragraphs without the gaps of a page. */
.zh-md > * + * {
    margin-top: 0.625rem;
}

/* A heading is the bubble's own size, a weight up: the reply stays a message, not a document. */
.zh-md__heading {
    color: var(--ink-1);
    font-weight: 600;
}

.zh-md__list {
    padding-left: 1.25rem;
}

/* Square bullets: the brand's square corners, at the list's own scale. */
ul.zh-md__list {
    list-style: square;
}

ol.zh-md__list {
    list-style: decimal;
}

.zh-md__list > li::marker {
    color: var(--ink-3);
}

.zh-md__list > li + li,
.zh-md__list > li > * + * {
    margin-top: 0.25rem;
}

/* Code in the mono face on the raised panel, framed by the hairline. */
.zh-md__code,
.zh-md__pre {
    border: 1px solid var(--hair);
    background: var(--panel-2);
    font-family: var(--font-mono);
}

.zh-md__code {
    padding: 0 0.25em;
    font-size: 0.875em;
}

/* A block of code keeps its lines and scrolls inside the bubble rather than wrapping or widening it. */
.zh-md__pre {
    overflow-x: auto;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    white-space: pre;
    overflow-wrap: normal;
}

.zh-md__rule {
    margin-inline: 0;
    border: 0;
    border-top: 1px solid var(--hair);
}
</style>
